/**
 * Staged UX progress for a fresh daily forecast.
 *
 * The forecast API answers only when the whole forecast is ready, so the percentage is an
 * elapsed-time narrative, not a share of generated tokens. It is deterministic, only grows,
 * stays below 100 while waiting and reaches 100 only after `complete()` (real API success).
 * Self-contained on purpose: no imports, so tests can load it directly.
 */

export type DailyGenerationStage = { from: number; title: string; support: string };

/** Every stage names only inputs the daily pipeline really uses: personal year, month, day and active interests. */
export const DAILY_GENERATION_STAGES: readonly DailyGenerationStage[] = [
  { from: 0, title: 'Собираю твои числа на сегодня', support: 'Начинаю с твоего личного ритма' },
  { from: 15, title: 'Сверяю личный год и месяц', support: 'Смотрю, какой фон задаёт текущий период' },
  { from: 30, title: 'Определяю ритм сегодняшнего дня', support: 'Здесь появляются первые важные акценты' },
  { from: 45, title: 'Сопоставляю энергии периода', support: 'Смотрю, что сегодня усиливается, а что лучше не торопить' },
  { from: 60, title: 'Ищу главные акценты именно для тебя', support: 'Оставляю только то, что может быть полезно именно сегодня' },
  { from: 74, title: 'Собираю всё в единый прогноз', support: 'Картина уже складывается' },
  { from: 88, title: 'Добавляю последние штрихи', support: 'Осталось совсем немного' },
];

export const DAILY_GENERATION_DONE = { title: 'Готово ✨', support: 'Твой прогноз на сегодня собран' } as const;

export const DAILY_GENERATION_LONG_WAIT = [
  'Прогноз получается особенно подробным - ещё немного ✨',
  'Я всё ещё здесь - заканчиваю твой прогноз',
] as const;

/** Elapsed ms at which the supporting line switches to the long-wait copy. */
export const LONG_WAIT_AFTER_MS = [12_000, 25_000] as const;

/** Index used for the completion copy; stages occupy 0..length-1. */
export const DONE_STAGE_INDEX = DAILY_GENERATION_STAGES.length;

/** Production daily forecasts take ~6 s (p50) and ~8 s (p90); the slow tail starts after the typical answer. */
const CURVE: readonly (readonly [ms: number, percent: number])[] = [
  [0, 3],
  [1_000, 15],
  [2_400, 30],
  [3_900, 45],
  [5_600, 60],
  [7_600, 74],
  [10_500, 88],
  [16_000, 94],
];
const TAIL_FROM = 94;
const TAIL_SPAN = 2;
const TAIL_TAU_MS = 20_000;

/** Highest value the waiting curve can reach; 100 is reserved for real success. */
export const WAITING_CAP = TAIL_FROM + TAIL_SPAN;

export const COMPLETE_ANIMATION_MS = 520;
export const COMPLETE_HOLD_MS = 480;
/** Tail of the hold during which the orb fades before the forecast takes its place. */
export const COMPLETE_LEAVE_MS = 240;

export function progressAt(elapsedMs: number): number {
  const t = Math.max(0, elapsedMs);
  for (let index = 1; index < CURVE.length; index += 1) {
    const [toMs, toPercent] = CURVE[index]!;
    if (t <= toMs) {
      const [fromMs, fromPercent] = CURVE[index - 1]!;
      return fromPercent + (toPercent - fromPercent) * ((t - fromMs) / (toMs - fromMs));
    }
  }
  const [lastMs] = CURVE[CURVE.length - 1]!;
  return TAIL_FROM + TAIL_SPAN * (1 - Math.exp(-(t - lastMs) / TAIL_TAU_MS));
}

export function stageIndexFor(percent: number): number {
  let found = 0;
  DAILY_GENERATION_STAGES.forEach((stage, index) => { if (percent >= stage.from) found = index; });
  return found;
}

export function longWaitLevelFor(elapsedMs: number): 0 | 1 | 2 {
  if (elapsedMs >= LONG_WAIT_AFTER_MS[1]) return 2;
  if (elapsedMs >= LONG_WAIT_AFTER_MS[0]) return 1;
  return 0;
}

export type GenerationProgressSnapshot = {
  /** Smooth value used for the bar width. */
  percentage: number;
  /** Integer shown as text; identical source as the bar. */
  displayPercent: number;
  stageIndex: number;
  startedAt: number;
  longWaitLevel: 0 | 1 | 2;
  isCompleting: boolean;
  isLeaving: boolean;
  /** 100% reached and the completion copy has been held long enough. */
  readyToReveal: boolean;
  title: string;
  support: string;
};

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

export class GenerationProgressController {
  readonly startedAt: number;
  #shown = 0;
  #completeFrom: number | null = null;
  #completeAt: number | null = null;

  constructor(startedAt: number) {
    this.startedAt = startedAt;
  }

  get isCompleting(): boolean {
    return this.#completeAt !== null;
  }

  /** Called only with a real successful API response. Idempotent. */
  complete(now: number): void {
    if (this.#completeAt !== null) return;
    this.tick(now);
    this.#completeFrom = this.#shown;
    this.#completeAt = now;
  }

  tick(now: number): GenerationProgressSnapshot {
    const elapsed = Math.max(0, now - this.startedAt);
    let target: number;
    let finished = false;
    let readyToReveal = false;
    let isLeaving = false;
    if (this.#completeAt === null) {
      target = Math.min(progressAt(elapsed), WAITING_CAP);
    } else {
      const since = Math.max(0, now - this.#completeAt);
      const t = Math.min(1, since / COMPLETE_ANIMATION_MS);
      const from = this.#completeFrom ?? this.#shown;
      target = t >= 1 ? 100 : from + (100 - from) * easeOutCubic(t);
      finished = t >= 1;
      readyToReveal = since >= COMPLETE_ANIMATION_MS + COMPLETE_HOLD_MS;
      isLeaving = since >= COMPLETE_ANIMATION_MS + COMPLETE_HOLD_MS - COMPLETE_LEAVE_MS;
    }
    this.#shown = Math.max(this.#shown, target);
    const completing = this.#completeAt !== null;
    const displayPercent = finished ? 100 : Math.min(completing ? 99 : Math.floor(WAITING_CAP), Math.floor(this.#shown));
    const stageIndex = completing ? DONE_STAGE_INDEX : stageIndexFor(this.#shown);
    const longWaitLevel = completing ? 0 : longWaitLevelFor(elapsed);
    const stage = DAILY_GENERATION_STAGES[Math.min(stageIndex, DAILY_GENERATION_STAGES.length - 1)]!;
    return {
      percentage: finished ? 100 : this.#shown,
      displayPercent,
      stageIndex,
      startedAt: this.startedAt,
      longWaitLevel,
      isCompleting: completing,
      isLeaving,
      readyToReveal,
      title: completing ? DAILY_GENERATION_DONE.title : stage.title,
      support: completing
        ? DAILY_GENERATION_DONE.support
        : longWaitLevel > 0 ? DAILY_GENERATION_LONG_WAIT[longWaitLevel - 1]! : stage.support,
    };
  }
}
